import { Backdrop, Box, CircularProgress, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import { StyledTable } from "../../../shared/ReusableTable/TableStyle";
import { setImportDataDetails } from "../../../store/Financials/importDataSlice";
import { RootState } from "../../../store/store";
import basestyle from "../../CSS/Base.module.css";
import { postFiscalExternalAccountSystemSubChartAccountsTransactionsForecast, saveFiscalExternalAccountSystem } from "../services/Financials/budget.service";
import { FiscalExternalAccountSystemVO } from "../services/Financials/financials";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer, SubSectionTitle } from "../StyledComponents/Container";
import CommonUtilities from "../Utils/CommonUtilities";
import { notifyUser } from "../Utils/notifyUser";
import { Column } from "./ReusableSubChartsAccountTable";

type VersionEntry = {
    id: number;
    version: number;
};

const BudgetImported = () => {
    const dispatch = useDispatch();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [tockenId, setTockenId] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const importData = useSelector((state: RootState) => state.importData);
    const [loadingFlag, setLoadingFlag] = useState(false);
    const year = importData?.year ?? "";
    const rowData = importData?.rowData ?? [];
    const forecastMappingDetails = importData?.forecastMappingDetails ?? [];
    const userDetails = useSelector((state: RootState) => state.auth);
    const transformedImport = importData?.transformedImport ?? [];
    const month = importData?.month ?? null;

    const [updatedVersionMap, setUpdatedVersionMap] = useState<Map<string, VersionEntry>>(new Map());
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const columns: Column[] = [
        { id: "locationGL", label: translate('locationGL'), minWidth: 150, align: "left" },
        { id: "amount", label: translate('amount'), minWidth: 150, align: "right" },
        { id: "dioceseGLMapping", label: translate('dioceseGLMapping'), minWidth: 250, align: "left" },
    ];
    const handleBack = () => {
        createRewuestPayload();
        navigate("/budgetImport");
    };
    async function handleSave() {
        setLoadingFlag(true);
        const request = createRewuestPayload();
        if (!request) {
            // createRewuestPayload already handles notification and loadingFlag
            return;
        }
        try {
            const responseList = await saveFiscalExternalAccountSystem(request);
            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                notifyUser(responseList?.data?.message, 'success', translate('importedBudget'));
                const versionMap = new Map<string, VersionEntry>();

                responseList.data?.data?.fiscalExternalAccountSystemVOList?.forEach((item: any) => {
                    if (item.description) {
                        versionMap.set(item.description, {
                            id: item.id,
                            version: item.version,
                        });
                    }
                });
                setUpdatedVersionMap(versionMap);
            } else {
                notifyUser(responseList?.data?.message, 'error', translate('importedBudget'));
            }
        } catch (error) {
            console.error(error);
            notifyUser(constants.ERROR_VALIDATION_MESSAGE_ERROR_OCCURRED_WHILE_POSTING_THE_EXCEL_SHEET_DETAILES, "error", translate("importedBudget"));
        } finally {
            setLoadingFlag(false);
        }
    }

    const createRewuestPayload = () => {
        const fiscalExternalAccountSystemVOList: FiscalExternalAccountSystemVO[] = [];
        const normalize = (str: string) => str?.toString().replace(/\s+/g, " ").trim();
        transformedImport.forEach((item: any) => {
            const mappingDetails = item.dioceseGLMapping;
            const matchedDetail = forecastMappingDetails.find(
                (detail: any) =>
                    normalize(detail.description) === normalize(item.locationGL)
            );
            const previousAccountNumber = matchedDetail?.previousAccountNumber != null ? matchedDetail.previousAccountNumber : matchedDetail?.accountNumber ?? "0";
            if (mappingDetails) {
                const newAccountNumber = mappingDetails.subChartAccountNumber;
                if (previousAccountNumber !== newAccountNumber) {
                    fiscalExternalAccountSystemVOList.push({
                        id: matchedDetail?.id ?? 0,
                        clientId: userDetails?.clientId,
                        participantId: userDetails.defaultParticipantId,
                        version: matchedDetail?.version ?? 0,
                        accountNumber: newAccountNumber,
                        previousAccountNumber: previousAccountNumber,
                        description: item.locationGL,
                    });
                } else if (matchedDetail?.previousAccountNumber != null && matchedDetail?.previousAccountNumber == newAccountNumber) {
                    fiscalExternalAccountSystemVOList.push({
                        id: matchedDetail?.id ?? 0,
                        clientId: userDetails?.clientId,
                        participantId: userDetails.defaultParticipantId,
                        version: matchedDetail?.version ?? 0,
                        accountNumber: newAccountNumber,
                        previousAccountNumber: "",
                        description: item.locationGL,
                    });
                }
                return;
            }
            if (!mappingDetails && previousAccountNumber != "" && previousAccountNumber != "0") {
                fiscalExternalAccountSystemVOList.push({
                    id: matchedDetail?.id ?? 0,
                    clientId: userDetails?.clientId,
                    participantId: userDetails.defaultParticipantId,
                    version: matchedDetail?.version ?? 0,
                    accountNumber: 0, // ✅ reset
                    previousAccountNumber: previousAccountNumber,
                    description: matchedDetail?.description ?? item.locationGL,
                });
                return;
            }
        });

        if (fiscalExternalAccountSystemVOList.length === 0) {
            notifyUser(constants.ERROR_VALIDATION_MESSAGE_NO_VALUE_HAVE_BEEN_MODIFIED, 'info', translate('importedBudget'));
            setLoadingFlag(false);
            return;
        }

        const request = {
            userName: userDetails.emailId,
            fiscalExternalAccountSystemVOList,
        };
        // Update forecastMappingDetails with the updated objects from request.fiscalExternalAccountSystemVOList
        const updatedForecastMappingDetails = importData.forecastMappingDetails.map((row) => {
            const updated = request.fiscalExternalAccountSystemVOList.find(
                (item) => item.description === row.description
            );
            if (updated) {
                return {
                    ...row,
                    accountNumber: updated.accountNumber,
                    previousAccountNumber: updated.previousAccountNumber,
                    id: updated.id,
                    version: updated.version,
                };
            }
            return row;
        });

        // Update Redux store with the updated forecastMappingDetails
        dispatch(setImportDataDetails({
            ...importData,
            forecastMappingDetails: updatedForecastMappingDetails
        }));
        return request;
    }

    async function handleImport() {
        setLoadingFlag(true);
        try {
            const normalize = (str: string) =>
                str?.toString().replace(/\s+/g, " ").trim();
            const SubChartTransForecastVO: any[] = [];
            transformedImport.forEach((row: any) => {
                const mappingDetails = row.dioceseGLMapping;

                const matchedDetail = forecastMappingDetails?.find(
                    (detail: any) =>
                        normalize(detail.description) === normalize(row.locationGL)
                );
                const previousAccountNumber = matchedDetail?.previousAccountNumber != null ? matchedDetail.previousAccountNumber : matchedDetail?.accountNumber ?? "0";

                let subChartAccountNo: string | null = null;
                if (mappingDetails) {
                    const newAccountNumber = mappingDetails.subChartAccountNumber;
                    // send only if changed
                    if (newAccountNumber !== null && newAccountNumber !== "") {
                        subChartAccountNo = newAccountNumber;
                    } else {
                        subChartAccountNo = "0";
                    }
                }
                else if (!mappingDetails && previousAccountNumber != "" && previousAccountNumber != "0") {
                    subChartAccountNo = "0"; // reset
                }
                const parseAmount = (value: any): number => {
                    if (value == null) return 0;
                    let str = value.toString()
                        .replace(/[−–—]/g, '-')
                        .replace(/,/g, '')
                        .replace(/\s+/g, '');

                    if (!str) return 0;
                    let isNegative = false;
                    if (str.startsWith('(') && str.endsWith(')')) {
                        isNegative = true;
                        str = str.slice(1, -1);
                    }

                    const parsed = Number(str);
                    return isNaN(parsed) ? 0 : (isNegative ? -parsed : parsed);
                };

                let monthsList: any[] = [];

                if (row.monthList && Array.isArray(row.monthList)) {
                    monthsList = row.monthList.map((m: any) => ({
                        month: m.name,
                        amount: parseAmount(m.amouunt),
                    }));
                } else {
                    monthsList = CommonUtilities.monthOrderList
                        .filter(month => row[month] !== undefined)
                        .map(month => ({
                            month,
                            amount: parseAmount(row[month]),
                        }));
                }

                const mapEntry = updatedVersionMap.get(row.locationGL);
                if (subChartAccountNo === "0" && previousAccountNumber != "" && previousAccountNumber != "0") {
                    SubChartTransForecastVO.push({
                        id: mapEntry?.id ?? matchedDetail?.id ?? 0,
                        version: mapEntry?.version ?? matchedDetail?.version ?? 0,
                        description: matchedDetail?.description ?? row.locationGL,
                        subChartAccountNo,
                        previousAccountNumber,
                        subChartTransForecastMonthsVOList: monthsList,
                    });
                } else if (subChartAccountNo && previousAccountNumber != "") {
                    SubChartTransForecastVO.push({
                        id: mapEntry?.id ?? matchedDetail?.id ?? 0,
                        version: mapEntry?.version ?? matchedDetail?.version ?? 0,
                        description: matchedDetail?.description ?? row.locationGL,
                        subChartAccountNo,
                        previousAccountNumber,
                        subChartTransForecastMonthsVOList: monthsList,
                    });
                }
            });

            if (SubChartTransForecastVO.length === 0) {
                notifyUser(constants.ERROR_VALIDATION_MESSAGE_NO_VALUE_HAVE_BEEN_MODIFIED, "info", translate("importedBudget"));
                setLoadingFlag(false);
                return;
            }

            const request = {
                participantId: userDetails.defaultParticipantId,
                financialYear: year,
                clientId: userDetails?.clientId,
                version: state?.version ?? 0,
                userName: userDetails.emailId,
                subChartTransForecastVOList: SubChartTransForecastVO,
            };
            const responseList = await postFiscalExternalAccountSystemSubChartAccountsTransactionsForecast(request);

            if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                notifyUser(responseList.data.message ?? translate('importSuccessfully'), 'success', translate('importedBudget'));
                navigate("/budgetListing");
            } else {
                notifyUser(responseList.data.message ?? translate('importFailed'), 'error', translate('importedBudget'));
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data ?? error.message);
                notifyUser(error.response?.data?.message ?? translate('apiRequestFailed'), 'error', translate('importedBudget'));
            } else {
                console.error(translate('unexpectedError'), error);
                notifyUser(translate('anUnexpectedError'), 'error', translate('importedBudget'));
            }
        } finally {
            setLoadingFlag(false);
        }
    }

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('importedBudget')} </SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        {rowData && (
                            <TableContainer sx={{ overflow: 'auto' }}>
                                <StyledTable
                                    stickyHeader
                                    aria-label="sticky table"
                                    size="small"
                                    sx={{
                                        [`& .${tableCellClasses.root}`]: {
                                            borderBottom: 'none',
                                            borderTop: 'none',
                                        },
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {columns.map((column) => (
                                                <TableCell key={column.id} align={column.align} className="cellTask">
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isLoading ? (
                                            <TableRow>
                                                <TableCell align="center" colSpan={columns.length}>
                                                    <div className="loading-container">
                                                        <CircularProgress />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            transformedImport.map((row: any, index: number) => {
                                                if (!row.locationGL || row.locationGL.trim() === "") {
                                                    return null;
                                                }
                                                return (
                                                    <TableRow key={row.id || index} sx={{ backgroundColor: index % 2 === 0 ? "#f1f3f4" : "#fff" }}>
                                                        {columns.map((column) => (
                                                            <TableCell key={column.id} align={column.align} className="left-aligned-text">
                                                                {column.id === "dioceseGLMapping" ? (
                                                                    row.dioceseGLMapping ? row.dioceseGLMapping.displayName : "-"
                                                                ) : column.id === "locationGL" ? (
                                                                    row.locationGL ?? "-"
                                                                ) : column.id === "amount" ? (
                                                                    row.amount ?? "0.00"
                                                                ) : (
                                                                    row[column.id] ?? "-"
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                );
                                            })
                                        )}


                                    </TableBody>
                                </StyledTable>
                            </TableContainer>
                        )}
                        <Box className={basestyle.buttoncontainer} sx={{ mt: 3 }}>
                            <ContainedButton onClick={handleBack}> {translate('back')}  </ContainedButton>
                            <ContainedButton onClick={handleSave}  > {translate('save')} </ContainedButton>
                            <ContainedButton onClick={handleImport}  > {translate('import')} </ContainedButton>
                        </Box>
                        <Backdrop
                            sx={{
                                color: "#fff",
                                zIndex: (theme) => theme.zIndex.drawer + 1,
                                display: "flex",
                                flexDirection: "column",
                            }}
                            open={loadingFlag}
                        >
                            <CircularProgress color="inherit" />
                            <SubSectionTitle  >{translate('pleasewait')}</SubSectionTitle>
                        </Backdrop>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>

    );
};
export default BudgetImported;